///
/// EarthBrowser
///
/// NEXRAD Radar Data Provider
///
/// Provides NEXRAD radar station data from local SQLite database with custom rendering
///
/// Created by Matt Giger on 1/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { NexradStationFeature } from '@/earthbrowser/dataset-distribution/nexrad/NexradTable'
import type { ColumnMetadata, DataQuery, DataRow, DataStats, FilterGroup, IDataProvider, ProviderCapabilities, QueryResult } from './types'

// Explicitly convert an arbitrary value to a string for comparison
function stringifyValue(value: unknown): string {
	if (typeof value === 'string') return value
	if (value === null || value === undefined) return String(value)
	if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value)
	if (value instanceof Date) return value.toString()
	if (Array.isArray(value)) return value.map(v => stringifyValue(v)).join(',')
	return JSON.stringify(value)
}

export class NexradDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'custom' as const
	private queryFn: () => Promise<NexradStationFeature[]>
	private cachedData: NexradStationFeature[] = []
	private isInitializedFlag = false

	constructor(config: {
		id: string
		name: string
		queryFn: () => Promise<NexradStationFeature[]>
	}) {
		this.id = config.id
		this.name = config.name
		this.queryFn = config.queryFn
	}

	async initialize(): Promise<void> {
		if (this.isInitializedFlag) return
		// Fetch initial data
		this.cachedData = await this.queryFn()
		this.isInitializedFlag = true
	}

	async refreshData(): Promise<void> {
		this.cachedData = await this.queryFn()
	}

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true,
		supportsRegex: true,
		supportsGeometry: true,
		supportsInsert: false,
		supportsUpdate: false,
		supportsDelete: false,
		supportsTransactions: false,
		supportsIndexing: false,
		supportsAggregation: false,
		supportsJoins: false,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	private columnMetadata: ColumnMetadata[] = [
		{
			id: 'station_id',
			name: 'Station',
			priority: 1,
			minWidth: 90,
			type: 'string',
			nullable: false,
			render: (value: unknown) => (
				<span className="font-mono text-sm font-medium text-green-400">{value as string}</span>
			)
		},
		{
			id: 'station_name',
			name: 'Name',
			priority: 1,
			minWidth: 160,
			type: 'string',
			nullable: false,
			render: (value: unknown) => (
				<div className="font-medium truncate max-w-[200px]" title={value as string}>
					{value as string}
				</div>
			)
		},
		{
			id: 'scans',
			name: 'Scans',
			priority: 3,
			minWidth: 80,
			type: 'number',
			nullable: false,
			render: (value: unknown) => {
				const scans = value as { scan_time: string }[]
				const count = scans?.length ?? 0
				return (
					<span className={count > 0 ? "text-cyan-400" : "text-white/40"}>
						{count > 0 ? `${count} scan${count > 1 ? 's' : ''}` : 'No data'}
					</span>
				)
			}
		},
		{
			id: 'latest_scan',
			name: 'Latest Scan',
			priority: 2,
			minWidth: 140,
			type: 'string',
			nullable: true,
			render: (_value: unknown, row: DataRow) => {
				const station = row as unknown as NexradStationFeature
				const latestScan = station.scans?.[0]
				if (!latestScan) return <span className="text-white/40">—</span>

				const scanTime = new Date(latestScan.scan_time)
				const now = new Date()
				const ageMinutes = Math.round((now.getTime() - scanTime.getTime()) / 60000)

				let ageText: string
				let colorClass: string
				if (ageMinutes < 10) {
					ageText = `${ageMinutes}m ago`
					colorClass = 'text-green-400'
				} else if (ageMinutes < 60) {
					ageText = `${ageMinutes}m ago`
					colorClass = 'text-yellow-400'
				} else if (ageMinutes < 1440) {
					const hours = Math.round(ageMinutes / 60)
					ageText = `${hours}h ago`
					colorClass = 'text-orange-400'
				} else {
					const days = Math.round(ageMinutes / 1440)
					ageText = `${days}d ago`
					colorClass = 'text-red-400'
				}

				return (
					<span className={colorClass}>{ageText}</span>
				)
			}
		},
		{
			id: 'max_reflectivity',
			name: 'Max dBZ',
			priority: 3,
			minWidth: 100,
			type: 'number',
			nullable: true,
			render: (_value: unknown, row: DataRow) => {
				const station = row as unknown as NexradStationFeature
				const latestScan = station.scans?.[0]
				const maxRef = latestScan?.max_reflectivity
				if (maxRef == null) return <span className="text-white/40">—</span>

				// Color based on reflectivity intensity
				let colorClass = 'text-blue-400'
				if (maxRef >= 50) colorClass = 'text-red-400'
				else if (maxRef >= 40) colorClass = 'text-orange-400'
				else if (maxRef >= 30) colorClass = 'text-yellow-400'
				else if (maxRef >= 20) colorClass = 'text-green-400'

				return (
					<span className={colorClass}>
						{maxRef.toFixed(1)} dBZ
					</span>
				)
			}
		},
		{
			id: 'coords',
			name: 'Location',
			priority: 4,
			minWidth: 150,
			type: 'string',
			nullable: false,
			render: (_value: unknown, row: DataRow) => {
				const station = row as unknown as NexradStationFeature
				return (
					<span className="text-white/50 font-mono text-xs">
						{station.lat.toFixed(2)}°, {station.lon.toFixed(2)}°
					</span>
				)
			}
		}
	]

	async getColumns(): Promise<ColumnMetadata[]> {
		return this.columnMetadata
	}

	async getStats(): Promise<DataStats> {
		return {
			totalRows: this.cachedData.length,
			columnCount: this.columnMetadata.length,
			memorySize: JSON.stringify(this.cachedData).length,
			lastModified: new Date()
		}
	}

	async query(query: DataQuery): Promise<QueryResult> {
		let filteredData = [...this.cachedData]

		// Apply full-text search
		if (query.search?.query) {
			const lowerSearch = query.search.query.toLowerCase()
			filteredData = filteredData.filter((row) => {
				// Search in station_id and station_name
				if (row.station_id.toLowerCase().includes(lowerSearch)) return true
				if (row.station_name.toLowerCase().includes(lowerSearch)) return true
				return false
			})
		}

		// Apply sorting
		if (query.sort && query.sort.length > 0) {
			const sortSpec = query.sort[0]
			const sortBy = sortSpec.column
			const sortDirection = sortSpec.direction

			filteredData.sort((a, b) => {
				let aVal: unknown
				let bVal: unknown

				if (sortBy === 'scans') {
					aVal = a.scans?.length ?? 0
					bVal = b.scans?.length ?? 0
				} else if (sortBy === 'latest_scan') {
					aVal = a.scans?.[0]?.scan_time ?? ''
					bVal = b.scans?.[0]?.scan_time ?? ''
				} else if (sortBy === 'max_reflectivity') {
					aVal = a.scans?.[0]?.max_reflectivity ?? -999
					bVal = b.scans?.[0]?.max_reflectivity ?? -999
				} else {
					aVal = a[sortBy as keyof NexradStationFeature]
					bVal = b[sortBy as keyof NexradStationFeature]
				}

				if (aVal == null && bVal == null) return 0
				if (aVal == null) return 1
				if (bVal == null) return -1

				let comparison = 0
				if (typeof aVal === 'string' && typeof bVal === 'string') {
					comparison = aVal.localeCompare(bVal)
				} else if (typeof aVal === 'number' && typeof bVal === 'number') {
					comparison = aVal - bVal
				} else {
					comparison = stringifyValue(aVal).localeCompare(stringifyValue(bVal))
				}

				return sortDirection === 'desc' ? -comparison : comparison
			})
		}

		// Calculate pagination
		const totalCount = filteredData.length
		const offset = query.offset || 0
		const limit = query.limit || 100
		const rows = filteredData.slice(offset, offset + limit)

		return {
			rows: rows as unknown as DataRow[],
			totalCount,
			offset,
			limit,
			hasMore: offset + limit < totalCount,
			executionTime: 0,
			fromCache: true
		}
	}

	async count(_filters?: FilterGroup): Promise<number> {
		return this.cachedData.length
	}

	async dispose(): Promise<void> {
		this.cachedData = []
		this.isInitializedFlag = false
	}
}

